import { useEffect, useState } from 'react';

import { Button, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { Checkbox, FormField, SearchField, Select } from '@ds/forms';
import { dados } from '@/data/porta';
import type { ContatoParaEscolha, ModeloDeFicha } from '@/data/tipos';

export interface PedidoDeAnotar {
    consentimento: true;
    modeloId?: string;
}

interface AnotarComVecchiaModalProps {
    open: boolean;
    onClose: () => void;
    /** A consulta foi aberta: o editor começa a escutar com este pedido. */
    onAberta: (atendimentoId: string, pedido: PedidoDeAnotar) => void;
}

const MODELO_PADRAO = '';

/**
 * Anotar com a VecchIA: a consulta presencial que começa pela escuta.
 *
 * Primeiro o paciente — só quem já está cadastrado, porque é no prontuário dele
 * que o resumo aprovado vai parar —, depois o consentimento. Confirmar abre a
 * consulta agora (`abrir_consulta_presencial`, 0157) e leva ao editor, que liga
 * o microfone.
 */
export function AnotarComVecchiaModal({ open, onClose, onAberta }: AnotarComVecchiaModalProps) {
    const [paciente, setPaciente] = useState<{ id: string; nome: string } | null>(null);
    const [busca, setBusca] = useState('');
    const [resultados, setResultados] = useState<ContatoParaEscolha[] | null>(null);
    const [modelos, setModelos] = useState<ModeloDeFicha[]>([]);
    const [modeloId, setModeloId] = useState(MODELO_PADRAO);
    const [consentimento, setConsentimento] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const [abrindo, setAbrindo] = useState(false);

    /* Abrir é recomeçar: a consulta de um paciente não herda o consentimento do anterior. */
    useEffect(() => {
        if (!open) return;
        setPaciente(null);
        setBusca('');
        setResultados(null);
        setModeloId(MODELO_PADRAO);
        setConsentimento(false);
        setErro(null);
        let vivo = true;
        dados().modelosDeFicha()
            .then((m) => { if (vivo) setModelos(m); })
            .catch(() => { if (vivo) setModelos([]); });
        return () => { vivo = false; };
    }, [open]);

    useEffect(() => {
        if (!open || paciente || busca.trim().length < 2) {
            setResultados(null);
            return;
        }
        let vivo = true;
        const t = window.setTimeout(() => {
            dados().buscarContatos(busca)
                .then((r) => { if (vivo) setResultados(r.filter((c) => c.tipo === 'paciente')); })
                .catch((e: unknown) => {
                    if (!vivo) return;
                    setResultados(null);
                    setErro(`A busca falhou: ${e instanceof Error ? e.message : 'tente de novo'}.`);
                });
        }, 250);
        return () => { vivo = false; window.clearTimeout(t); };
    }, [open, busca, paciente]);

    const comecar = async () => {
        if (!paciente) {
            setErro('Escolha o paciente que vai receber o resumo.');
            return;
        }
        if (!consentimento) {
            setErro('Confirme que o paciente autorizou a gravação.');
            return;
        }
        setAbrindo(true);
        setErro(null);
        try {
            const atendimentoId = await dados().abrirConsultaPresencial(paciente.id);
            onAberta(atendimentoId, { consentimento: true, ...(modeloId ? { modeloId } : {}) });
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível abrir a consulta.');
        } finally {
            setAbrindo(false);
        }
    };

    return (
        <Modal
            open={open} width={520}
            title="Anotar com VecchIA"
            description="Para a consulta presencial: a VecchIA escuta a conversa, preenche a ficha e escreve o resumo. Você revisa antes de ir para o prontuário. O áudio não fica guardado."
            dirty={Boolean(paciente) || consentimento}
            onClose={onClose}
            footer={(
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button
                        iconLeft={<Icon name="mic" size={15} />} loading={abrindo}
                        disabled={!paciente || !consentimento}
                        onClick={() => void comecar()}
                    >
                        Começar a escutar
                    </Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <FormField label="Paciente" required hint="O resumo aprovado vai para a aba Prontuário deste paciente.">
                    {paciente ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 42, padding: '0 6px 0 12px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-control)', background: 'var(--surface)' }}>
                            <Icon name="user-round" size={15} style={{ color: 'var(--neutral-500)' }} />
                            <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{paciente.nome}</span>
                            <Button size="sm" variant="ghost" onClick={() => { setPaciente(null); setBusca(''); }}>Trocar</Button>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <SearchField
                                value={busca} width="100%" ariaLabel="Buscar paciente"
                                placeholder="Buscar paciente por nome ou telefone"
                                onChange={(v) => { setBusca(v); setErro(null); }}
                            />
                            {resultados && resultados.length > 0 && (
                                <ul aria-label="Pacientes encontrados" style={{ listStyle: 'none', margin: 0, padding: 0, border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                                    {resultados.map((c) => (
                                        <li key={c.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                                            <button
                                                type="button"
                                                onClick={() => { setPaciente({ id: c.id, nome: c.nome }); setResultados(null); }}
                                                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 40, padding: '8px 12px', border: 0, background: 'var(--surface)', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-ui)', fontSize: 13.5, color: 'var(--text-primary)' }}
                                            >
                                                <span style={{ flex: 1, minWidth: 0, fontWeight: 600 }}>{c.nome}</span>
                                                <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>{c.telefone}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {resultados && resultados.length === 0 && (
                                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                    Nenhum paciente com “{busca.trim()}”. Cadastre o paciente antes de anotar a consulta.
                                </p>
                            )}
                        </div>
                    )}
                </FormField>

                <FormField label="Ficha que a VecchIA preenche">
                    <Select
                        value={modeloId} onChange={setModeloId}
                        options={[
                            { value: MODELO_PADRAO, label: 'Nota da consulta (queixa, história, exame, avaliação, conduta)' },
                            ...modelos.filter((m) => m.nome !== 'Nota da consulta').map((m) => ({ value: m.id, label: m.nome })),
                        ]}
                    />
                </FormField>

                <Checkbox
                    checked={consentimento} onChange={setConsentimento}
                    label="O paciente autorizou a gravação desta consulta"
                    description="O consentimento fica registrado na auditoria, com o seu nome e o horário."
                />

                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
            </div>
        </Modal>
    );
}
